import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible, areAllAccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {
  ProcessAsyncRecordsQuery,
  ProcessAsyncRecordsQueryVariables,
} from 'processes/modals/__generated/ProcessAsyncRecordsQuery';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import {assertNever} from 'util/Types';

type ProcessAsyncRecordOrInaccessibleInfo = ProcessAsyncRecordsQuery['processAsyncRecords'][number];

export const PROCESS_ASYNC_RECORDS_QUERY = gql`
  query ProcessAsyncRecordsQuery($processAsyncRecordIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processAsyncRecords(ids: $processAsyncRecordIds) {
      ... on ProcessAsyncRecordNode {
        id
        asyncData {
          ... on ProcessStartAutomatedMappingRecordNode {
            id
            blockedMappersAndExpressions {
              schemaMapper {
                id
                toSchema {
                  ... on ProcessTaskSchemaNode {
                    id
                    name
                  }
                  ... on ProcessOutputSchemaNode {
                    id
                    name
                  }
                  ...InaccessibleInfoGraphQL
                }
              }
            }
          }
          ... on ProcessFlowchartSchemaMappingRecordNode {
            id
            schemaMapper {
              id
              toSchema {
                ... on ProcessTaskSchemaNode {
                  id
                  name
                }
                ... on ProcessOutputSchemaNode {
                  id
                  name
                }
                ...InaccessibleInfoGraphQL
              }
            }
          }
          ... on ProcessFlowchartExecuteSchemaMapperRecordNode {
            id
            schemaMapper {
              id
              toSchema {
                ... on ProcessTaskSchemaNode {
                  id
                  name
                }
                ... on ProcessOutputSchemaNode {
                  id
                  name
                }
                ...InaccessibleInfoGraphQL
              }
            }
          }
        }
        details
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface AutomatedMappingIssuesTableModalWithDataProps {
  processAsyncRecords: Array<ProcessAsyncRecordOrInaccessibleInfo>;
  onRequestClose: () => void;
  isLoading: false;
}

interface AutomatedMappingIssuesTableModalStillLoadingProps {
  processAsyncRecords?: never;
  onRequestClose: () => void;
  isLoading: true;
}

type AutomatedMappingIssuesTableModalProps =
  | AutomatedMappingIssuesTableModalWithDataProps
  | AutomatedMappingIssuesTableModalStillLoadingProps;

function AutomatedMappingIssuesTableModal(props: AutomatedMappingIssuesTableModalProps): JSX.Element {
  const getAsyncRecordMappingToText = (record: ProcessAsyncRecordOrInaccessibleInfo): string => {
    if (isInaccessible(record)) {
      return NO_ACCESS_TEXT;
    }
    switch (record.asyncData.__typename) {
      case 'ProcessFlowchartExecuteSchemaMapperRecordNode':
      case 'ProcessFlowchartSchemaMappingRecordNode':
        return isInaccessible(record.asyncData.schemaMapper.toSchema)
          ? NO_ACCESS_TEXT
          : (record.asyncData.schemaMapper.toSchema as {name: string}).name;
      case 'ProcessStartAutomatedMappingRecordNode': {
        const schemas = record.asyncData.blockedMappersAndExpressions.map(
          ({schemaMapper}) => schemaMapper.toSchema
        );
        return areAllAccessible(schemas) ? schemas.map(({name}) => name).join(', ') : NO_ACCESS_TEXT;
      }
      default:
        assertAndContinue(false, `Unexpected async record data ${assertNever(record.asyncData)}`);
        return NO_ACCESS_TEXT;
    }
  };

  return (
    <Modal title="Automated mapping issues" enableRedesign={true} onRequestClose={props.onRequestClose}>
      {props.isLoading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <table
          className={classNames(
            css(styles.tableWrapper),
            'bulkImportModal-table table table-striped selectable'
          )}
        >
          <colgroup>
            <col className={css(styles.indexCol)} />
            <col className={css(styles.destinationCol)} />
            <col className={css(styles.messageCol)} />
          </colgroup>
          <thead>
            <tr>
              <th>Issue</th>
              <th>Destination schema(s)</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {props.processAsyncRecords.map((processAsyncRecord, index) => (
              <tr
                key={
                  isInaccessible(processAsyncRecord) ? processAsyncRecord.invalidId : processAsyncRecord.id
                }
              >
                <td>{index + 1}</td>
                <td>{getAsyncRecordMappingToText(processAsyncRecord)}</td>
                <td>{isInaccessible(processAsyncRecord) ? NO_ACCESS_TEXT : processAsyncRecord.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

interface QueryingAutomatedMappingIssuesTableModalProps {
  processAsyncRecordIds: Array<string>;
  onRequestClose: () => void;
}

export default function QueryingAutomatedMappingIssuesTableModal(
  props: QueryingAutomatedMappingIssuesTableModalProps
): JSX.Element | null {
  const processAsyncRecordQuery = useBenchlingQuery<
    ProcessAsyncRecordsQuery,
    ProcessAsyncRecordsQueryVariables
  >(PROCESS_ASYNC_RECORDS_QUERY, {
    variables: {processAsyncRecordIds: props.processAsyncRecordIds},
    fetchPolicy: 'network-only',
  });

  if (processAsyncRecordQuery.type === QueryResultType.SUCCESS) {
    return (
      <AutomatedMappingIssuesTableModal
        isLoading={false}
        processAsyncRecords={processAsyncRecordQuery.data.processAsyncRecords}
        onRequestClose={props.onRequestClose}
      />
    );
  } else if (processAsyncRecordQuery.type === QueryResultType.PENDING) {
    return <AutomatedMappingIssuesTableModal isLoading={true} onRequestClose={props.onRequestClose} />;
  } else {
    notifications.error('Could not load automated mapping issues table modal. Please try again.');
    return null;
  }
}

const styles = StyleSheet.create({
  tableWrapper: {
    tableLayout: 'fixed',
    width: '100%',
    overflowWrap: 'break-word',
  },

  indexCol: {
    width: '10%',
  },

  destinationCol: {
    width: '27%',
  },

  messageCol: {
    width: '63%',
  },
});
